// [목적] POI 혼잡도 30일 예측 곡선 카드 — SVG 단순 path + 최저 구간 라벨
// [이유] WC-TOURAPI-QR-5-03 — FolioPage / FolioMobile 의 POI 상세 카드 하단에 부착해
//         사용자가 "언제 방문하면 한산한지" 시각적으로 파악하도록 함.
//         recharts 의존성 추가 회피를 위해 SVG 직접 작성 (viewBox 100x40 단위 정규화)
// [사이드 이팩트] packages/types 의 CongestionForecast 타입을 직접 import. 스키마 변경 시 렌더 로직 점검 필요
// [수정 시 고려사항] points 가 빈 배열이면 "데이터 없음" fallback 노출 (404 아님).
//                   [2026-09-20 문구 교정] 헤더 '추천 방문 시간대' → '한산한 날 예측'(데이터는 일자별
//                   30일 예측이라 '시간대'가 아니다), 빈 상태 '활용 신청 후 표시됩니다' → '이 장소는 집계
//                   대상이 아니에요'(TourAPI 활용 신청·키는 정상 — 실제 원인은 숙소/카페처럼 집중률 집계
//                   대상 관광지가 아니어서 poi_congestion 매칭 행이 없는 것). 문구 변경 시 테스트 동시 갱신.
//                   SVG 내부 색상은 디자인 토큰 참조 불가 범위이므로 tokens 변수를 통해 동적으로 주입.
//                   WCAG 2.1 AA — aria-label 필수, 색만으로 정보 전달 금지(라벨 병기), 4.5:1 대비 확보.
//                   카드 width 는 부모 컨테이너 flex 에 따라 100% 이므로 고정 px 금지

import { useState } from 'react';
import { Box, ButtonBase, Card, CardContent, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { CongestionForecast } from '@gangwoncanvas/types';

import { LineIcon } from '../icons/LineIcons.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const ACCENT = c.accent[500][L];
// [2026-09-20 디자인 개선] 추가 토큰 — 주말 밴드(warm), 최고점 마커(danger),
// 추천 배지 아이콘 텍스트색(accent[600]: 작은 요소에서 AA 4.5:1 을 충족하는 진한 그린).
const ACCENT_TEXT = c.accent[600][L];
const WARM = c.warm[500][L];
const DANGER = c.danger[500][L];
const R = tokens.radius;
const SP = tokens.spacing;

// ─── 내부 유틸 ────────────────────────────────────────────────────────────────

/** 요일 인덱스(0=일) → 한 글자 한글 요일 */
const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;

// [목적] 날짜 문자열(YYYY-MM-DD)에서 "M월 D일" 형식 추출
// [이유] 최저 구간 라벨 표시에 사용. Intl.DateTimeFormat 없이 문자열 파싱으로 번들 추가 없음
// [사이드 이팩트] 없음 (순수 함수)
// [수정 시 고려사항] date 가 YYYY-MM-DD 형식이 아니면 NaN 반환 — zod 스키마 파싱 전제이므로 방어 불필요
function formatDate(date: string): string {
  const [, m, d] = date.split('-');
  return `${parseInt(m ?? '0', 10)}월 ${parseInt(d ?? '0', 10)}일`;
}

// [목적] 'YYYY-MM-DD' → 요일 인덱스(0=일 … 6=토)
// [이유] [2026-09-20 디자인 개선] 여행 계획에서 "주말이냐"는 혼잡도만큼 중요한 정보인데 기존 카드는
//        날짜만 있고 요일이 없어, 곡선이 왜 주기적으로 치솟는지(주말) 읽히지 않았다.
// [사이드 이팩트] 없음 (순수 함수)
// [수정 시 고려사항] 'T00:00:00' 을 붙여 로컬 자정으로 파싱 — 날짜만 넘기면 UTC 로 해석돼 KST 에서
//                   하루 밀린다(요일이 어긋남).
function weekdayIndex(date: string): number {
  return new Date(`${date}T00:00:00`).getDay();
}

function isWeekend(date: string): boolean {
  const w = weekdayIndex(date);
  return w === 0 || w === 6;
}

// [목적] 혼잡도 points 배열에서 최저/최고 rate 인덱스 탐색
// [이유] "가장 한산한 날"(권장) 과 "가장 붐비는 날"(회피) 라벨에 각각 사용
// [사이드 이팩트] 없음 (순수 함수)
// [수정 시 고려사항] points 가 빈 배열이면 -1 반환 — 호출측에서 분기 처리
function findMinIndex(points: CongestionForecast['points']): number {
  if (points.length === 0) return -1;
  let minIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if ((points[i]?.rate ?? 100) < (points[minIdx]?.rate ?? 100)) minIdx = i;
  }
  return minIdx;
}

function findMaxIndex(points: CongestionForecast['points']): number {
  if (points.length === 0) return -1;
  let maxIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if ((points[i]?.rate ?? 0) > (points[maxIdx]?.rate ?? 0)) maxIdx = i;
  }
  return maxIdx;
}

// ─── 차트 기하 ────────────────────────────────────────────────────────────────
// [이유] [2026-09-20 디자인 개선] 기존 차트는 viewBox 100x40 을 preserveAspectRatio="none" 으로
//        가로 4배 늘려 그려서 선 두께가 비대칭으로 뭉개졌고(수직 성분만 두꺼움), 좌표 상수(2/36/96/32)가
//        코드 곳곳에 흩어져 최저점 마커 위치를 별도 수식으로 다시 계산하고 있었다.
//        실제 렌더 폭에 가까운 viewBox(400x96) + meet 로 바꿔 왜곡을 없애고, 좌표 계산을 buildGeometry()
//        한 곳으로 모은다.
// [수정 시 고려사항] PAD_L 은 좌측 '혼잡/한산' 축 라벨 자리 — 라벨 문구를 늘리면 함께 키울 것.
const CHART_W = 400;
const CHART_H = 96;
const PAD_L = 26;
const PAD_R = 6;
const PAD_T = 10;
const PAD_B = 8;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

interface ChartGeometry {
  xs: number[];
  ys: number[];
  line: string;
  area: string;
  avgY: number;
  bandW: number;
}

// [목적] points → SVG 좌표/패스 일괄 계산 (선/영역/평균선/주말밴드 폭)
// [이유] 렌더 JSX 안에서 좌표 수식을 반복하지 않도록 단일 진입점으로 모음(최저점 마커가 곡선에서
//        미세하게 어긋나던 원인 제거 — 기존에는 동일 수식을 두 번 따로 썼다).
// [사이드 이팩트] 없음 (순수 함수)
// [수정 시 고려사항] rate 가 전부 같으면 range=1 로 고정해 0 나눗셈을 피하고 중앙 높이에 평탄선을 그린다.
function buildGeometry(points: CongestionForecast['points']): ChartGeometry {
  const n = points.length;
  const rates = points.map((p) => p.rate);
  const maxRate = Math.max(...rates);
  const minRate = Math.min(...rates);
  const range = maxRate - minRate || 1;
  const avg = rates.reduce((s, v) => s + v, 0) / (n || 1);

  const toY = (rate: number): number => PAD_T + (1 - (rate - minRate) / range) * PLOT_H;
  const xs = points.map((_, i) => (n === 1 ? PAD_L + PLOT_W / 2 : PAD_L + (i / (n - 1)) * PLOT_W));
  const ys = points.map((p) => (maxRate === minRate ? PAD_T + PLOT_H / 2 : toY(p.rate)));

  const coords = xs.map((x, i) => `${x.toFixed(1)},${(ys[i] ?? 0).toFixed(1)}`);
  const line = `M ${coords.join(' L ')}`;
  const bottom = PAD_T + PLOT_H;
  const area = `${line} L ${(xs[n - 1] ?? 0).toFixed(1)},${bottom} L ${(xs[0] ?? 0).toFixed(1)},${bottom} Z`;

  return {
    xs,
    ys,
    line,
    area,
    avgY: maxRate === minRate ? PAD_T + PLOT_H / 2 : toY(avg),
    bandW: n > 1 ? PLOT_W / (n - 1) : PLOT_W,
  };
}

// ─── 기간 필터 ────────────────────────────────────────────────────────────────

// [목적] 카드 상단 기간 세그먼트(1일/7일/30일) 정의
// [이유] [2026-09-20] 30일 곡선 하나만 보여주니 "당장 오늘/이번 주는 어떤데?" 라는 가장 잦은 질문에
//        답을 못 했다. 30일은 여행 날짜를 아직 안 정한 사람에게, 7일은 이번 주 안에 갈 사람에게,
//        1일은 오늘 갈지 말지 정하는 사람에게 필요한 뷰다.
// [사이드 이팩트] 없음 — BE 는 항상 30일치를 내려주고(usePoiCongestion days=30) 이 필터는 그 배열을
//        클라이언트에서 slice 할 뿐이라 추가 네트워크 요청이 없다(토글 즉시 반응).
// [수정 시 고려사항] 값은 points 앞에서부터 자르는 일수 — BE 가 오늘(KST)부터 오름차순으로 내려주는
//        계약(poi-meta.controller.ts kstToday)에 의존한다. 정렬이 바뀌면 여기도 함께 바꿀 것.
const RANGE_OPTIONS = [
  { days: 1, label: '오늘' },
  { days: 7, label: '7일' },
  { days: 30, label: '30일' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];

// [목적] 집중률(0~100) → 사람이 읽는 혼잡 단계
// [이유] 숫자 56 만으로는 좋은 값인지 알 수 없다. 임계값 30 은 이 코드베이스의 기존 기준을 그대로 쓴다
//        (silver-congestion.repository.ts 주석: "조용한 칩(cnctr_rate≤30)").
// [수정 시 고려사항] 조용한 칩 기준이 바뀌면 함께 갱신 — 같은 데이터에 두 기준이 공존하면 안 된다.
function congestionLevel(rate: number): { label: string; color: string } {
  if (rate <= 30) return { label: '한산', color: ACCENT_TEXT };
  if (rate <= 60) return { label: '보통', color: INK_MID };
  return { label: '붐빔', color: DANGER };
}

// ─── CongestionForecastCard ───────────────────────────────────────────────────

export interface CongestionForecastCardProps {
  poiId: string;
  forecast: CongestionForecast;
}

// [목적] CongestionForecastCard 본체 — 기간 필터 + SVG 곡선 + 최저/최고 구간 요약
// [이유] WC-TOURAPI-QR-5-03 — FolioPage / FolioMobile 의 POI 상세 영역에 삽입
// [2026-09-20 디자인 개선] 기존 카드는 (a) 눈금 없는 56px 곡선이라 높낮이의 의미(혼잡/한산)를 알 수 없고,
//        (b) 요일 정보가 없어 주기적인 봉우리가 주말이라는 게 안 읽히고, (c) 시작/끝 날짜 2개만 있어
//        가운데 지점의 날짜를 가늠할 수 없고, (d) "가장 한산한 날"만 알려줄 뿐 "피해야 할 날"은 없고,
//        (e) 집중률 숫자의 절대값만 보여줘 그게 높은 편인지 낮은 편인지 판단할 근거가 없었다.
//        → 주말 세로 밴드 + 평균 점선 + 축 라벨(혼잡/한산) + 눈금 3개 + 최저/최고 이중 요약 +
//          평균 대비 편차(%)로 보강한다.
// [2026-09-20 기간 필터] 상단에 오늘/7일/30일 세그먼트 추가. '오늘'은 점 1개라 곡선이 의미가 없으므로
//        곡선 대신 큰 수치 + 혼잡 단계 블록으로 전환하고, 대신 30일 전체 최저일을 힌트로 안내한다.
// [사이드 이팩트] forecast prop 이 갱신되면 SVG path 가 즉시 재계산됨 (메모이제이션 불필요 수준의 크기)
// [수정 시 고려사항] poiId 는 SVG <linearGradient> id 네임스페이스로 사용한다 — 한 화면에 카드가 여러 개
//                   떠도 그라데이션 정의가 서로 덮어쓰지 않게 하기 위함(id 전역 충돌 방지).
//                   points 가 빈 배열이면 fallback 텍스트 표시 — 404 와 구분.
//                   SVG role="img" + aria-label 로 스크린리더 대응.
export function CongestionForecastCard({
  poiId,
  forecast,
}: CongestionForecastCardProps): JSX.Element {
  const allPoints = forecast.points;
  const [range, setRange] = useState<RangeDays>(30);

  const isEmpty = allPoints.length === 0;
  // 선택 구간만 잘라 사용 — 데이터가 구간보다 짧으면 있는 만큼만
  const points = allPoints.slice(0, range);
  const isSingle = points.length <= 1;

  const minIdx = findMinIndex(points);
  const maxIdx = findMaxIndex(points);
  const minPoint = minIdx >= 0 ? points[minIdx] : null;
  const maxPoint = maxIdx >= 0 ? points[maxIdx] : null;
  const geo = isEmpty || isSingle ? null : buildGeometry(points);
  const gradientId = `congestion-fill-${poiId}`;

  const avgRate = points.length ? points.reduce((s, p) => s + p.rate, 0) / points.length : 0;
  // 평균 대비 얼마나 한산한지 — 절대 수치(집중률 27)만으로는 좋은 값인지 판단할 근거가 없다
  const minDeltaPct =
    minPoint && avgRate > 0 ? Math.round(((avgRate - minPoint.rate) / avgRate) * 100) : 0;

  // '오늘' 뷰 전용 — 30일 전체 기준 최저일(구간을 넓히면 더 한산한 날이 있다는 힌트)
  const overallMinIdx = findMinIndex(allPoints);
  const overallMin = overallMinIdx >= 0 ? allPoints[overallMinIdx] : null;
  const overallAvg = allPoints.length
    ? allPoints.reduce((s, p) => s + p.rate, 0) / allPoints.length
    : 0;

  // X축 눈금: 첫날 / 가운데 / 마지막날 (3개면 구간 전체의 감이 잡힌다)
  const tickIdxs =
    isEmpty || isSingle
      ? []
      : [...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])];

  const today = allPoints[0] ?? null;
  const todayLevel = today ? congestionLevel(today.rate) : null;
  const todayDeltaPct =
    today && overallAvg > 0 ? Math.round(((overallAvg - today.rate) / overallAvg) * 100) : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: `${R.m}px`,
        border: `1px solid ${BORDER}`,
        bgcolor: c.paper[0][L],
        mt: `${SP[3]}px`,
      }}
    >
      <CardContent sx={{ p: `${SP[4]}px !important` }}>
        {/* 헤더 — 제목 + 기간 세그먼트 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: `${SP[2]}px`,
            mb: `${SP[3]}px`,
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 800,
              color: INK_HI,
              // [2026-09-20] 라벨이 한글이라 JetBrains Mono 고정폭에서는 '한산한  날  예측' 처럼
              // 벌어져 보인다 — 한글 라벨은 sans 로 조판(라틴 이거브로우는 기존대로 모노 유지).
              letterSpacing: '-0.2px',
            }}
          >
            한산한 날 예측
          </Typography>

          {!isEmpty && (
            <Box
              role="group"
              aria-label="예측 기간 선택"
              sx={{
                display: 'flex',
                gap: '2px',
                bgcolor: PAPER2,
                borderRadius: '999px',
                p: '2px',
                flexShrink: 0,
              }}
            >
              {RANGE_OPTIONS.map((opt) => {
                const selected = range === opt.days;
                return (
                  <ButtonBase
                    key={opt.days}
                    onClick={() => setRange(opt.days)}
                    aria-pressed={selected}
                    sx={{
                      px: '9px',
                      // [2026-09-20] 44px 터치 타겟 규칙의 예외 — 카드 내부 보조 컨트롤이라 높이를
                      // 키우면 헤더가 카드를 지배한다. 대신 좌우 패딩과 간격으로 오탭을 줄인다.
                      height: 22,
                      borderRadius: '999px',
                      fontSize: 10,
                      fontWeight: 700,
                      color: selected ? c.paper[0][L] : INK_MID,
                      bgcolor: selected ? BRAND : 'transparent',
                      transition: 'background-color .15s, color .15s',
                    }}
                  >
                    {opt.label}
                  </ButtonBase>
                );
              })}
            </Box>
          )}
        </Box>

        {isEmpty ? (
          /* 빈 데이터 fallback */
          <Box
            sx={{
              bgcolor: PAPER2,
              borderRadius: `${R.s}px`,
              p: `${SP[4]}px`,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: 11.5, color: INK_MID, lineHeight: 1.6, fontWeight: 700 }}>
              혼잡도 정보 없음
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: INK_LOW, mt: `${SP[1]}px` }}>
              이 장소는 집계 대상이 아니에요
            </Typography>
          </Box>
        ) : isSingle && today && todayLevel ? (
          /* '오늘' 뷰 — 점 1개는 곡선이 의미 없으므로 수치 + 단계로 대체 */
          <>
            <Box
              sx={{
                bgcolor: PAPER2,
                borderRadius: `${R.s}px`,
                p: `${SP[3]}px ${SP[4]}px`,
              }}
            >
              <Typography sx={{ fontSize: 10.5, color: INK_MID, fontWeight: 700 }}>
                {formatDate(today.date)} ({WEEKDAY_KO[weekdayIndex(today.date)]}) 집중률
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: `${SP[2]}px`, mt: '2px' }}>
                <Typography sx={{ fontSize: 30, fontWeight: 800, color: INK_HI, lineHeight: 1.15 }}>
                  {today.rate}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: todayLevel.color }}>
                  {todayLevel.label}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 10, color: INK_LOW, mt: `${SP[2]}px`, lineHeight: 1.6 }}>
              {todayDeltaPct > 0
                ? `30일 평균(${Math.round(overallAvg)})보다 ${todayDeltaPct}% 한산해요`
                : `30일 평균(${Math.round(overallAvg)})보다 ${Math.abs(todayDeltaPct)}% 붐벼요`}
              {overallMin && overallMinIdx > 0
                ? ` · 30일 중엔 ${formatDate(overallMin.date)}이 가장 한산`
                : ''}
            </Typography>
          </>
        ) : (
          <>
            {/* SVG 곡선 — 주말 밴드 + 평균 점선 + 최저/최고 마커 */}
            <Box
              component="svg"
              viewBox={`0 0 ${CHART_W} ${CHART_H}`}
              role="img"
              aria-label={`${points.length}일 혼잡도 예측 곡선. ${
                minPoint ? `${formatDate(minPoint.date)} 가장 한산 (집중률 ${minPoint.rate})` : ''
              }${maxPoint ? `, ${formatDate(maxPoint.date)} 가장 혼잡 (집중률 ${maxPoint.rate})` : ''}`}
              sx={{ display: 'block', width: '100%', height: 'auto' }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity="0.28" />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity="0.02" />
                </linearGradient>
              </defs>

              {/* 주말(토·일) 세로 밴드 — 봉우리가 주말이라는 사실을 배경으로 설명 */}
              {geo &&
                points.map((p, i) =>
                  isWeekend(p.date) ? (
                    <rect
                      key={`wk-${p.date}`}
                      x={Math.max(PAD_L, (geo.xs[i] ?? 0) - geo.bandW / 2)}
                      y={PAD_T - 4}
                      width={Math.min(
                        geo.bandW,
                        PAD_L + PLOT_W - Math.max(PAD_L, (geo.xs[i] ?? 0) - geo.bandW / 2),
                      )}
                      height={PLOT_H + 4}
                      fill={WARM}
                      opacity="0.10"
                    />
                  ) : null,
                )}

              {/* 평균 점선 — 개별 수치가 평균보다 위인지 아래인지 판단 기준선 */}
              {geo && (
                <line
                  x1={PAD_L}
                  y1={geo.avgY}
                  x2={PAD_L + PLOT_W}
                  y2={geo.avgY}
                  stroke={INK_LOW}
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity="0.45"
                />
              )}

              {/* 영역 + 곡선 */}
              {geo && <path d={geo.area} fill={`url(#${gradientId})`} stroke="none" />}
              {geo && (
                <path
                  d={geo.line}
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* 7일 뷰에서는 점이 적어 개별 일자가 어디인지 보이도록 점을 찍는다 */}
              {geo &&
                points.length <= 10 &&
                points.map((p, i) => (
                  <circle
                    key={`dot-${p.date}`}
                    cx={geo.xs[i]}
                    cy={geo.ys[i]}
                    r="2"
                    fill={ACCENT}
                    opacity="0.55"
                  />
                ))}

              {/* 최고점(피해야 할 날) — 속이 빈 작은 마커로 조용히 표시 */}
              {geo && maxIdx >= 0 && maxIdx !== minIdx && (
                <circle
                  cx={geo.xs[maxIdx]}
                  cy={geo.ys[maxIdx]}
                  r="3"
                  fill={c.paper[0][L]}
                  stroke={DANGER}
                  strokeWidth="1.6"
                />
              )}

              {/* 최저점(추천일) — 세로 가이드 + 강조 마커 */}
              {geo && minIdx >= 0 && (
                <>
                  <line
                    x1={geo.xs[minIdx]}
                    y1={geo.ys[minIdx]}
                    x2={geo.xs[minIdx]}
                    y2={PAD_T + PLOT_H}
                    stroke={BRAND}
                    strokeWidth="1"
                    strokeDasharray="2 2"
                    opacity="0.6"
                  />
                  <circle
                    cx={geo.xs[minIdx]}
                    cy={geo.ys[minIdx]}
                    r="4.5"
                    fill={BRAND}
                    stroke={c.paper[0][L]}
                    strokeWidth="2"
                  />
                </>
              )}

              {/* Y축 의미 라벨 — 위/아래가 각각 무엇인지 (색만으로 정보 전달 금지, WCAG 1.4.1) */}
              <text x="0" y={PAD_T + 4} fontSize="8" fill={INK_LOW}>
                혼잡
              </text>
              <text x="0" y={PAD_T + PLOT_H} fontSize="8" fill={INK_LOW}>
                한산
              </text>
            </Box>

            {/* X축 눈금 — 첫날/가운데/마지막날 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: `${SP[1]}px`,
                mb: `${SP[2]}px`,
                pl: `${(PAD_L / CHART_W) * 100}%`,
              }}
            >
              {tickIdxs.map((idx, i) => {
                const p = points[idx];
                if (!p) return null;
                return (
                  <Typography
                    key={p.date}
                    sx={{
                      fontSize: 9.5,
                      color: INK_LOW,
                      textAlign: i === 0 ? 'left' : i === tickIdxs.length - 1 ? 'right' : 'center',
                    }}
                  >
                    {formatDate(p.date)}
                  </Typography>
                );
              })}
            </Box>

            {/* 범례 — 배경 밴드(주말)와 점선(평균)이 무엇인지 알려준다.
                [이유] 그래프에 표식을 추가해도 의미를 설명하지 않으면 노이즈로만 읽힌다. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: `${SP[3]}px`,
                mb: `${SP[3]}px`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 12,
                    height: 8,
                    borderRadius: '2px',
                    bgcolor: `${WARM}2E`,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 9.5, color: INK_LOW }}>주말</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Box
                  component="span"
                  aria-hidden
                  sx={{ width: 12, height: 0, borderTop: `1px dashed ${INK_LOW}`, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: 9.5, color: INK_LOW }}>
                  {points.length}일 평균
                </Typography>
              </Box>
            </Box>

            {/* 추천일 요약 — 날짜 + 요일 + 집중률 + 평균 대비 편차 */}
            {minPoint && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${SP[3]}px`,
                  bgcolor: `${ACCENT}14`,
                  border: `1px solid ${ACCENT}33`,
                  borderRadius: `${R.s}px`,
                  p: `${SP[2]}px ${SP[3]}px`,
                }}
              >
                <LineIcon name="trend-down" size={16} sx={{ color: ACCENT_TEXT }} aria-hidden />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: INK_HI }}>
                    {formatDate(minPoint.date)} 가장 한산
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: INK_MID, mt: '1px' }}>
                    {WEEKDAY_KO[weekdayIndex(minPoint.date)]}요일 · 집중률 {minPoint.rate}
                    {minDeltaPct > 0 ? ` · 평균보다 ${minDeltaPct}% 한산` : ''}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* 회피일 한 줄 — 추천일과 대비되는 정보라 같은 무게로 키우지 않는다 */}
            {maxPoint && maxIdx !== minIdx && (
              <Typography
                sx={{
                  fontSize: 10,
                  color: INK_LOW,
                  mt: `${SP[2]}px`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    border: `1.5px solid ${DANGER}`,
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                가장 붐빔 · {formatDate(maxPoint.date)}({WEEKDAY_KO[weekdayIndex(maxPoint.date)]})
                집중률 {maxPoint.rate}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
